import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, AlertCircle, Loader2, Clock } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { api, apiErrorMessage } from '../apiClient';
import { toast } from 'sonner';

export default function OrgApplyPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [orgName, setOrgName] = useState('');
  const [contactPerson, setContactPerson] = useState(user?.full_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [emailX, setEmailX] = useState(user?.email || '');
  const [desc, setDesc] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [approved, setApproved] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!orgName.trim()) return setErr('Organization name is required');
    if (!contactPerson.trim()) return setErr('Contact person is required');
    if (!phone.trim() || phone.trim().length < 6) return setErr('Enter a valid phone');
    if (!emailX.trim()) return setErr('Contact email is required');
    if (!desc.trim()) return setErr('Add a short business description');
    setSubmitting(true);
    try {
      await api.post('/organizer/apply', {
        organization_name: orgName.trim(),
        contact_person: contactPerson.trim(),
        contact_phone: phone.trim(),
        contact_email: emailX.trim(),
        business_description: desc.trim(),
        website: website.trim() || undefined,
      });
      await refreshUser();
      setApproved(true);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setSubmitting(false); }
  };

  if (approved) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center px-6" data-testid="apply-success">
        <div className="w-24 h-24 rounded-full bg-emerald-500 flex items-center justify-center mb-4">
          <Clock className="w-11 h-11 text-white" />
        </div>
        <h2 className="text-2xl font-black text-gray-900">Application submitted!</h2>
        <p className="text-[15px] text-gray-500 text-center mt-2 max-w-sm">
          {orgName} is under review by our team. We'll notify you once approved (usually within 24 hours).
        </p>
        <button
          data-testid="apply-goto-dashboard-btn"
          onClick={() => navigate('/')}
          className="mt-6 bg-brand-magenta hover:bg-pink-700 text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-colors"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-6 py-4" data-testid="org-apply-page">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="apply-back-btn" onClick={() => navigate(-1)} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-6 h-6 text-gray-900" />
        </button>
        <h2 className="text-base font-black text-gray-900">Apply as Organizer</h2>
        <div className="w-6" />
      </div>

      <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-tight">List your events<br />on RADIUS</h1>
      <p className="text-sm text-gray-500 mt-2 mb-4 leading-relaxed">We'll review your application and notify you once approved.</p>

      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Organization name">
          <input data-testid="apply-orgname-input" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g. Vibe Events" className="input-field" />
        </Field>
        <Field label="Contact person">
          <input data-testid="apply-contact-input" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} placeholder="Your full name" className="input-field" />
        </Field>
        <Field label="Contact phone">
          <input data-testid="apply-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" type="tel" className="input-field" />
        </Field>
        <Field label="Contact email">
          <input data-testid="apply-email-input" value={emailX} onChange={(e) => setEmailX(e.target.value)} placeholder="contact@brand.com" type="email" className="input-field" />
        </Field>
        <Field label="Business description">
          <textarea data-testid="apply-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Tell us what kind of events you organize" rows={4} className="input-field resize-none" />
        </Field>
        <Field label="Website (optional)">
          <input data-testid="apply-website-input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." className="input-field" />
        </Field>

        {err && (
          <div data-testid="apply-error" className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm font-semibold">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {err}
          </div>
        )}

        <button
          data-testid="apply-submit-btn"
          type="submit"
          disabled={submitting}
          className="w-full bg-brand-magenta hover:bg-pink-700 disabled:opacity-70 text-white py-4 rounded-2xl font-black text-base transition-colors mt-2"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Submit Application'}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-bold text-gray-500">{label}</label>
      {children}
    </div>
  );
}
